/**
 * Encoding profiles: the one place a codec/container pair is defined.
 *
 * An admin picks a PROFILE, never a raw MIME string and never an independent
 * codec + container. "VP9 in MP4" and "H.264 in WebM" are combinations no
 * browser here records and no player here reads; letting them be expressed at
 * all just moves the failure to recording time.
 *
 * MEASURED, not assumed (Chromium 151, this repo's Playwright browser):
 *
 *   video/webm;codecs=vp8,opus            supported   Infinity duration
 *   video/webm;codecs=vp9,opus            supported   Infinity duration
 *   video/mp4;codecs=avc1.42E01E,opus     supported   FINITE duration
 *   video/mp4;codecs=avc1.42E01E,mp4a.40.2  NOT supported
 *
 * That last line is why each profile carries a LIST of candidate MIME strings.
 * AAC is the conventional MP4 audio codec and the one most players expect, so
 * it is tried first — but this Chromium refuses it while accepting Opus in the
 * same container. Hardcoding either one alone would have been wrong.
 */
export const RECORDING_PROFILES = {
    'vp8-webm': {
        id: 'vp8-webm',
        label: 'VP8 / WebM',
        codec: 'VP8',
        container: 'webm',
        extension: 'webm',
        mimeCandidates: (audio) => (audio ? ['video/webm;codecs=vp8,opus'] : ['video/webm;codecs=vp8']),
        reportsFiniteDuration: false,
    },
    'vp9-webm': {
        id: 'vp9-webm',
        label: 'VP9 / WebM',
        codec: 'VP9',
        container: 'webm',
        extension: 'webm',
        mimeCandidates: (audio) => (audio ? ['video/webm;codecs=vp9,opus'] : ['video/webm;codecs=vp9']),
        reportsFiniteDuration: false,
    },
    'h264-mp4': {
        id: 'h264-mp4',
        label: 'H.264 / MP4',
        codec: 'H.264',
        container: 'mp4',
        extension: 'mp4',
        // avc1.42E01E is Baseline 3.0. Chromium reports back the level it actually
        // chose (avc1.420020), which is why the SELECTED mime is read off the
        // recorder rather than assumed from what was requested.
        mimeCandidates: (audio) => audio
            ? ['video/mp4;codecs=avc1.42E01E,mp4a.40.2', 'video/mp4;codecs=avc1.42E01E,opus']
            : ['video/mp4;codecs=avc1.42E01E'],
        reportsFiniteDuration: true,
    },
};
export const ALL_PROFILE_IDS = Object.keys(RECORDING_PROFILES);
/**
 * Preference order for `auto`, and the order the fallback ladder walks.
 *
 * VP9 first because it measured smallest by a wide margin on the code-editor
 * content that matters here (223 kbps vs VP8's 306 and H.264's 319 at the same
 * 500 kbps request). VP8 second because it is the universal baseline. H.264
 * last: it compressed WORST of the three, so it exists for playback
 * compatibility, not for bandwidth.
 */
export const AUTO_PREFERENCE = [
    'vp9-webm',
    'vp8-webm',
    'h264-mp4',
];
export const isProfileId = (v) => v === 'auto' || (typeof v === 'string' && v in RECORDING_PROFILES);
export const defaultSupportProbe = (m) => {
    try {
        return typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(m);
    }
    catch (_a) {
        return false;
    }
};
/**
 * THE capability resolver. `isTypeSupported` is called here and nowhere else,
 * so there is one answer to "can this browser record X" rather than one per
 * caller.
 */
export const getSupportedRecordingProfiles = (audio, probe = defaultSupportProbe) => ALL_PROFILE_IDS.map((id) => {
    var _a;
    const p = RECORDING_PROFILES[id];
    const attempted = p.mimeCandidates(audio).map((mimeType) => ({
        mimeType,
        supported: probe(mimeType),
    }));
    const hit = attempted.find((a) => a.supported);
    return {
        id,
        label: p.label,
        supported: Boolean(hit),
        mimeType: (_a = hit === null || hit === void 0 ? void 0 : hit.mimeType) !== null && _a !== void 0 ? _a : null,
        attempted,
    };
});
/** Resolve one profile to the MIME the browser will actually take, or null. */
export const resolveProfileMime = (id, audio, probe = defaultSupportProbe) => { var _a; return (_a = RECORDING_PROFILES[id].mimeCandidates(audio).find((m) => probe(m))) !== null && _a !== void 0 ? _a : null; };
